import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, RefreshCw, Filter } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTransactionSync } from '../../hooks/useTransactionSync';

const AdminTrades = () => {
  const { syncTrigger } = useTransactionSync();
  const [trades, setTrades] = useState([]);
  const [topAssets, setTopAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sideFilter, setSideFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchTrades();
    fetchTopAssets();
  }, [page, sideFilter]);

  // Listen for sync events from Transaction Control
  useEffect(() => {
    if (syncTrigger > 0) {
      console.log('🔄 AdminTrades: Syncing due to transaction change');
      fetchTrades();
      fetchTopAssets();
    }
  }, [syncTrigger]);

  const fetchTrades = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const BACKEND_URL = '';
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      
      if (sideFilter) params.append('side', sideFilter);

      const response = await fetch(
        `${BACKEND_URL}/api/admin/dashboard/trades?${params}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();
      setTrades(data.trades || []);
      setTotalPages(data.pages || 1);
    } catch (error) {
      console.error('Error fetching trades:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTopAssets = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const BACKEND_URL = '';

      const response = await fetch(
        `${BACKEND_URL}/api/admin/dashboard/top-assets`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();
      setTopAssets(data.assets || []);
    } catch (error) {
      console.error('Error fetching top assets:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Trade Analytics</h1>
          <p className="text-cyan-400">Monitor trading activity and asset performance</p>
        </div>
        <button
          onClick={() => { fetchTrades(); fetchTopAssets(); }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 transition-all"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Top Assets Chart */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-cyan-400" />
          Top 3 Traded Assets
        </h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={topAssets}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
            <XAxis dataKey="asset" stroke="#22d3ee" />
            <YAxis stroke="#22d3ee" tickFormatter={(value) => `$${value.toLocaleString()}`} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1e293b',
                border: '1px solid #22d3ee',
                borderRadius: '8px',
                color: '#fff'
              }}
              formatter={(value) => [`$${value.toLocaleString()}`, 'Volume']}
            />
            <Legend />
            <Bar dataKey="volume" fill="#22d3ee" name="Trading Volume" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Filter */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl p-6">
        <div className="flex items-center gap-4">
          <Filter className="w-5 h-5 text-cyan-400" />
          <select
            value={sideFilter}
            onChange={(e) => { setSideFilter(e.target.value); setPage(1); }}
            className="px-4 py-2 bg-slate-800 border border-cyan-500/30 rounded-lg text-white focus:outline-none focus:border-cyan-400"
          >
            <option value="">All Trades</option>
            <option value="buy">Buy Only</option>
            <option value="sell">Sell Only</option>
          </select>
        </div>
      </div>

      {/* Trades Table */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-800 border-b border-cyan-500/30">
              <tr>
                <th className="px-6 py-4 text-left text-cyan-400 font-semibold">Trade ID</th>
                <th className="px-6 py-4 text-left text-cyan-400 font-semibold">User</th>
                <th className="px-6 py-4 text-left text-cyan-400 font-semibold">Pair</th>
                <th className="px-6 py-4 text-left text-cyan-400 font-semibold">Type</th>
                <th className="px-6 py-4 text-left text-cyan-400 font-semibold">Amount</th>
                <th className="px-6 py-4 text-left text-cyan-400 font-semibold">Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  </td>
                </tr>
              ) : trades.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-cyan-600">
                    No trades found
                  </td>
                </tr>
              ) : (
                trades.map((trade) => (
                  <tr key={trade.trade_id} className="border-b border-cyan-500/10 hover:bg-slate-800/50">
                    <td className="px-6 py-4 text-white font-mono text-sm">
                      {trade.trade_id.substring(0, 12)}...
                    </td>
                    <td className="px-6 py-4 text-cyan-400">{trade.username}</td>
                    <td className="px-6 py-4 text-white">{trade.pair || 'N/A'}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        trade.type === 'buy' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {trade.type === 'buy' ? (
                          <><TrendingUp className="w-3 h-3 inline mr-1" />BUY</>
                        ) : (
                          <><TrendingDown className="w-3 h-3 inline mr-1" />SELL</>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-white font-semibold">
                      ${trade.amount?.toFixed(2) || '0.00'}
                    </td>
                    <td className="px-6 py-4 text-cyan-400 text-sm">
                      {formatDate(trade.created_at || trade.timestamp)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-cyan-500/30">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-cyan-400">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTrades;